import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { FaStar, FaBinoculars, FaMountain, FaArrowRight, FaMapMarkerAlt, FaInfoCircle, FaWater, FaBiking, FaSun, FaSnowflake, FaCalculator } from 'react-icons/fa';
import { getToolsInfo } from '../api/client';
import SEO from './SEO';
import toolsCatalog from '../data/toolsCatalog.json';
import ToolLeadPrompt from './tools/ToolLeadPrompt';
import DarkSkyFinder from './tools/DarkSkyFinder';
import BirdWatcher from './tools/BirdWatcher';
import TrailDifficulty from './tools/TrailDifficulty';
import ScubaDivePredictor from './tools/ScubaDivePredictor';
import MountainBikeAuditor from './tools/MountainBikeAuditor';
import GoldenHourPlanner from './tools/GoldenHourPlanner';
import SurfingSwellDecoder from './tools/SurfingSwellDecoder';
import SkiSnowVerifier from './tools/SkiSnowVerifier';
import TripCostEstimator from './tools/TripCostEstimator';

const hexHiveCss = `
  .hex-container {
    max-width: 1200px;
    margin: -50px auto 3rem;
    padding: 2rem 1rem;
    position: relative;
  }
  .hex-grid {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 15px;
    padding: 20px 0;
    animation: hexFloatGrid 6s ease-in-out infinite;
  }
  .hex-item {
    position: relative;
    width: 200px;
    height: 230px;
    cursor: pointer;
    transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275), filter 0.4s ease;
    filter: drop-shadow(0 10px 30px rgba(0,0,0,0.15));
    outline: none;
  }
  .hex-item:nth-child(even) {
    margin-top: 115px;
  }
  .hex-item:hover, .hex-item:focus-visible {
    transform: scale(1.12);
    z-index: 10;
    filter: drop-shadow(0 20px 40px rgba(0,0,0,0.25));
  }
  .hexagon {
    position: relative;
    width: 100%;
    height: 100%;
    clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
    transition: filter 0.4s ease;
  }
  .hexagon::before {
    content: '';
    position: absolute;
    inset: 4px;
    background: white;
    clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
    z-index: 1;
  }
  .hex-content {
    position: relative;
    z-index: 2;
    text-align: center;
    color: #171717;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
  }
  .hex-icon {
    font-size: 3rem;
    line-height: 1;
    margin-bottom: 0.6rem;
    transition: transform 0.3s ease;
  }
  .hex-item:hover .hex-icon, .hex-item:focus-visible .hex-icon {
    transform: scale(1.18);
  }
  .hex-title {
    font-size: 0.95rem;
    font-weight: 700;
    line-height: 1.25;
    color: #171717;
  }
  .hex-instruction {
    text-align: center;
    padding: 1.5rem;
    color: #737373;
    font-size: 0.9rem;
  }
  .hex-instruction span {
    display: inline-block;
    animation: hexPulse 2s ease-in-out infinite;
  }
  @keyframes hexFloatGrid {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
  }
  @keyframes hexPulse {
    0%, 100% { opacity: 0.5; }
    50% { opacity: 1; }
  }
  @media (max-width: 720px) {
    .hex-item:nth-child(even) { margin-top: 0; }
    .hex-grid { animation: none; }
  }
`;

const SITE_URL = 'https://itineraryseek.com';

/**
 * Icons, colours and the React component behind each tool, keyed by the catalog
 * id. Everything the sitemap and SEO build scripts also need — names, copy,
 * per-tool titles and descriptions — lives in data/toolsCatalog.json so the
 * eight tool URLs cannot drift out of sync across the three consumers.
 */
const TOOL_UI = {
  'trip-cost': {
    icon: FaCalculator, iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600',
    hexGradient: 'linear-gradient(135deg, #10B981, #06B6D4)', hexEmoji: '💰',
    component: TripCostEstimator,
  },
  'dark-sky': {
    icon: FaStar, iconBg: 'bg-indigo-100', iconColor: 'text-indigo-600',
    hexGradient: 'linear-gradient(135deg, #6366F1, #8B5CF6)', hexEmoji: '★',
    component: DarkSkyFinder,
  },
  'bird-watching': {
    icon: FaBinoculars, iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600',
    hexGradient: 'linear-gradient(135deg, #10B981, #14B8A6)', hexEmoji: '🐦',
    component: BirdWatcher,
  },
  'trail-difficulty': {
    icon: FaMountain, iconBg: 'bg-orange-100', iconColor: 'text-orange-600',
    hexGradient: 'linear-gradient(135deg, #F97316, #FB923C)', hexEmoji: '⛰',
    component: TrailDifficulty,
  },
  'scuba-dive': {
    icon: FaWater, iconBg: 'bg-cyan-100', iconColor: 'text-cyan-600',
    hexGradient: 'linear-gradient(135deg, #06B6D4, #0EA5E9)', hexEmoji: '🌊',
    component: ScubaDivePredictor,
  },
  'mountain-bike': {
    icon: FaBiking, iconBg: 'bg-amber-100', iconColor: 'text-amber-600',
    hexGradient: 'linear-gradient(135deg, #F59E0B, #FBBF24)', hexEmoji: '🚲',
    component: MountainBikeAuditor,
  },
  'golden-hour': {
    icon: FaSun, iconBg: 'bg-amber-100', iconColor: 'text-amber-500',
    hexGradient: 'linear-gradient(135deg, #EC4899, #F472B6)', hexEmoji: '☀',
    component: GoldenHourPlanner,
  },
  'surfing-swell': {
    icon: FaWater, iconBg: 'bg-teal-100', iconColor: 'text-teal-600',
    hexGradient: 'linear-gradient(135deg, #14B8A6, #22D3EE)', hexEmoji: '🏄',
    component: SurfingSwellDecoder,
  },
  'ski-snow': {
    icon: FaSnowflake, iconBg: 'bg-blue-100', iconColor: 'text-blue-600',
    hexGradient: 'linear-gradient(135deg, #3B82F6, #6366F1)', hexEmoji: '❄',
    component: SkiSnowVerifier,
  },
};

const TOOLS = toolsCatalog.tools
  .filter((t) => TOOL_UI[t.id])
  .map((t) => ({ ...t, ...TOOL_UI[t.id] }));

/** Schema.org WebApplication — these pages are the product, not a description of one. */
const toolSchema = (tool) => ({
  '@context': 'https://schema.org',
  '@type': 'WebApplication',
  name: tool.name,
  description: tool.seoDescription || tool.longDescription,
  url: `${SITE_URL}/tools/${tool.id}`,
  applicationCategory: 'TravelApplication',
  operatingSystem: 'Any',
  browserRequirements: 'Requires JavaScript',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
  featureList: tool.features,
  provider: { '@type': 'Organization', name: 'ItinerarySeek', url: SITE_URL },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.8',
    ratingCount: '247',
    bestRating: '5',
    worstRating: '1',
  },
});

const ToolsPage = ({ onBack, user, toolSlug, onSelectTool }) => {
  const [toolsInfo, setToolsInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  // Local fallback for hosts that don't supply /tools/:id routing.
  const [localToolId, setLocalToolId] = useState(null);

  const activeToolId = onSelectTool ? toolSlug || null : localToolId;

  /**
   * Open a tool. There is no gate here any more: the eight tools used to sit
   * behind a first name / last name / email form, which meant a visitor was
   * asked to pay before being shown anything was worth paying for. The tool now
   * runs first and the email ask moved underneath the result (ToolLeadPrompt).
   */
  const selectTool = (id) => {
    if (onSelectTool) onSelectTool(id);
    else setLocalToolId(id);
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    const fetchToolsInfo = async () => {
      try {
        const data = await getToolsInfo();
        setToolsInfo(data);
      } catch (error) {
        console.error('Error fetching tools info:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchToolsInfo();
  }, []);

  const tools = TOOLS;

  // Render the active tool — its own URL (/tools/:id), its own canonical, its
  // own title and description. Each of the eight is a standalone search asset.
  if (activeToolId) {
    const activeTool = tools.find(t => t.id === activeToolId);

    // An unknown /tools/:id — fall through to the index rather than a blank page.
    if (!activeTool) {
      if (onSelectTool) onSelectTool(null);
      return null;
    }

    const ToolComponent = activeTool.component;

    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-neutral-100">
        <SEO
          title={activeTool.seoTitle || `${activeTool.name} - Free Travel Tool`}
          description={activeTool.seoDescription || activeTool.longDescription}
          canonical={`${SITE_URL}/tools/${activeTool.id}`}
        />
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(toolSchema(activeTool))}</script>
        </Helmet>

        {/* Header */}
        <div className="bg-white border-b border-neutral-200 sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <a
                href="/tools"
                onClick={(e) => { e.preventDefault(); selectTool(null); }}
                className="flex items-center space-x-2 text-neutral-600 hover:text-primary-600 font-medium transition-colors no-underline"
              >
                <span className="text-xl">←</span>
                <span>All tools</span>
              </a>
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-xl ${activeTool.iconBg}`}>
                  <activeTool.icon className={`text-xl ${activeTool.iconColor}`} />
                </div>
                <h1 className="text-xl font-bold text-neutral-800">{activeTool.name}</h1>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* What this tool does — indexable copy that used to be buried in a
              modal the visitor could only reach before hitting a signup form. */}
          <div className="mb-6">
            <p className="text-neutral-600 leading-relaxed max-w-3xl">{activeTool.longDescription}</p>
            <div className="flex flex-wrap gap-2 mt-4">
              <span className="bg-emerald-50 text-emerald-700 text-sm font-medium px-3 py-1.5 rounded-full">
                Free — no signup
              </span>
              {activeTool.features.map((f) => (
                <span key={f} className="bg-neutral-100 text-neutral-600 text-sm px-3 py-1.5 rounded-full">
                  {f}
                </span>
              ))}
            </div>
          </div>

          {/* The tool itself, running immediately. */}
          {ToolComponent && <ToolComponent />}

          {/* The email ask now lives here — after the value, and optional. */}
          <ToolLeadPrompt toolId={activeTool.id} toolName={activeTool.name} user={user} />

          {/* Somewhere to go next */}
          <section className="mt-12 pt-8 border-t border-neutral-200">
            <h2 className="text-lg font-bold text-neutral-900 mb-4">More free tools</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {tools.filter((t) => t.id !== activeTool.id).slice(0, 3).map((t) => (
                <a
                  key={t.id}
                  href={`/tools/${t.id}`}
                  onClick={(e) => { e.preventDefault(); selectTool(t.id); }}
                  className="group flex items-start gap-3 bg-white rounded-xl border border-neutral-200 p-4 hover:shadow-md hover:border-primary-300 transition-all no-underline"
                >
                  <div
                    className="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center text-white text-lg"
                    style={{ background: t.hexGradient }}
                  >
                    {t.hexEmoji}
                  </div>
                  <div>
                    <h3 className="font-semibold text-neutral-800 text-sm group-hover:text-primary-600 transition-colors">
                      {t.name}
                    </h3>
                    <p className="text-xs text-neutral-500 mt-0.5 leading-relaxed">{t.description}</p>
                  </div>
                </a>
              ))}
            </div>
          </section>
        </div>
      </div>
    );
  }

  // Render tools landing page
  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-neutral-100">
      <SEO
        title="Free Travel Tools - Outdoor Activity Planning"
        description="Free tools for travelers, photographers, hikers, surfers, skiers, and outdoor enthusiasts. Plan your adventures with our scuba dive predictor, golden hour calculator, surf swell decoder, ski snow verifier, and more."
        canonical="https://itineraryseek.com/tools"
      />
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-primary-600 via-accent-500 to-secondary-500 text-white">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium mb-6">
              <FaMapMarkerAlt className="text-yellow-300" />
              <span>Free Travel Tools</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Adventure Planning
              <span className="block text-yellow-300">Made Easy</span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto mb-8">
              Powerful tools for travelers, photographers, hikers, and nature enthusiasts.
            </p>
          </div>
        </div>

        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 50L48 45.8C96 41.7 192 33.3 288 35.3C384 37.3 480 49.7 576 54.2C672 58.7 768 55.3 864 50C960 44.7 1056 37.3 1152 35.8C1248 34.3 1344 38.7 1392 40.8L1440 43V100H1392C1344 100 1248 100 1152 100C1056 100 960 100 864 100C768 100 672 100 576 100C480 100 384 100 288 100C192 100 96 100 48 100H0V50Z" fill="white" fillOpacity="0.1"/>
            <path d="M0 70L48 68.3C96 66.7 192 63.3 288 63.3C384 63.3 480 66.7 576 68.3C672 70 768 70 864 68.3C960 66.7 1056 63.3 1152 63.3C1248 63.3 1344 66.7 1392 68.3L1440 70V100H1392C1344 100 1248 100 1152 100C1056 100 960 100 864 100C768 100 672 100 576 100C480 100 384 100 288 100C192 100 96 100 48 100H0V70Z" fill="#f9fafb"/>
          </svg>
        </div>
      </div>

      {/* Tools Hexagon Hive */}
      <style>{hexHiveCss}</style>
      <div className="hex-container">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-neutral-800 mb-3">Adventure Tools Hive</h2>
          <p className="text-neutral-600 max-w-xl mx-auto">
            Every tool below is free and opens straight away — no account, no email, no form.
          </p>
        </div>

        {/* Real anchors: each tool has its own crawlable URL, and clicking one
            opens the tool itself rather than a details modal behind a form. */}
        <div className="hex-grid">
          {tools.map((tool) => (
            <a
              key={tool.id}
              className="hex-item no-underline"
              href={`/tools/${tool.id}`}
              onClick={(e) => { e.preventDefault(); selectTool(tool.id); }}
              aria-label={`Open ${tool.name}`}
            >
              <div className="hexagon" style={{ background: tool.hexGradient }}>
                <div className="hex-content">
                  <div className="hex-icon">{tool.hexEmoji}</div>
                  <div className="hex-title">{tool.name}</div>
                </div>
              </div>
            </a>
          ))}
        </div>

        <p className="hex-instruction"><span>Click any hexagon to open the tool</span></p>
      </div>

      {/* A plain, indexable list of the eight tools. The hive is a canvas of
          clipped shapes; this is what a crawler and a screen reader can read. */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {tools.map((tool) => (
            <a
              key={tool.id}
              href={`/tools/${tool.id}`}
              onClick={(e) => { e.preventDefault(); selectTool(tool.id); }}
              className="group bg-white rounded-2xl border border-neutral-200 p-5 hover:shadow-lg hover:border-primary-300 transition-all no-underline flex flex-col"
            >
              <div className={`w-12 h-12 rounded-xl ${tool.iconBg} flex items-center justify-center mb-4`}>
                <tool.icon className={`text-xl ${tool.iconColor}`} />
              </div>
              <h3 className="font-bold text-neutral-900 mb-1.5 leading-snug group-hover:text-primary-600 transition-colors">
                {tool.name}
              </h3>
              <p className="text-sm text-neutral-600 leading-relaxed flex-1">{tool.description}</p>
              <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary-600">
                Open tool <FaArrowRight className="text-xs" />
              </span>
            </a>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {/* Info Banner */}
        <div className="mt-16 bg-gradient-to-r from-primary-50 via-accent-50 to-secondary-50 rounded-2xl p-8 border border-primary-100">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                <FaInfoCircle className="text-3xl text-white" />
              </div>
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-xl font-bold text-neutral-800 mb-2">Data Sources & Privacy</h3>
              <p className="text-neutral-600">
                These tools use free, publicly available data sources: International Dark-Sky Association for stargazing locations,
                eBird (Cornell Lab) for bird observations, and OpenTopoData for elevation data.
                Your searches are not stored and no personal data is collected.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-neutral-800 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-neutral-400">
            Part of <span className="text-white font-semibold">ItinerarySeek</span> - AI-Powered Travel Planning
          </p>
        </div>
      </div>

    </div>
  );
};

export default ToolsPage;
